/**
 * Device.ts — WebGPU adapter/device initialization.
 *
 * Responsibilities:
 *  - Request adapter and device with sensible feature/limit defaults.
 *  - Configure canvas context with the preferred format (sRGB-aware).
 *  - Track canvas resize via ResizeObserver (DPR-aware) and notify listeners.
 *  - Handle `device.lost` with user-visible diagnostic.
 *
 * No singletons: the device is returned to the caller and threaded through frame context.
 */

export interface GpuContext {
  readonly adapter: GPUAdapter;
  readonly device: GPUDevice;
  readonly canvas: HTMLCanvasElement;
  readonly context: GPUCanvasContext;
  readonly canvasFormat: GPUTextureFormat;
  /** Current backing pixel size (already DPR-multiplied). */
  size: { width: number; height: number };
  /** Subscribe to resize events. Returns an unsubscribe function. */
  onResize(cb: (w: number, h: number) => void): () => void;
  /** Tear down ResizeObserver, drop listeners, destroy the GPU device. */
  dispose(): void;
}

export async function initGpu(canvas: HTMLCanvasElement): Promise<GpuContext> {
  if (!("gpu" in navigator)) {
    throw new Error(
      "WebGPU is not available in this browser. Use Chrome/Edge 113+.",
    );
  }

  const adapter = await navigator.gpu.requestAdapter({
    powerPreference: "high-performance",
  });
  if (!adapter) {
    throw new Error("No WebGPU adapter found.");
  }

  const device = await adapter.requestDevice({
    label: "CastOffPro primary device",
  });

  // DEV diagnostic: count how many devices we create. If this ever logs
  // "#2" or higher, something is creating extra GPUDevices (which all
  // get the same label) and we have the "swapchain view from Device X
  // cannot be used with Device X" validation flood.
  if (import.meta.env.DEV) {
    const w = window as unknown as { __castoffproDeviceCount?: number };
    w.__castoffproDeviceCount = (w.__castoffproDeviceCount ?? 0) + 1;
    console.log(`[CastOffPro] GPUDevice #${w.__castoffproDeviceCount} created`, device);
  }

  device.lost.then((info) => {
    // Surface to the page; the user sees something instead of a frozen canvas.
    console.error("[WebGPU] Device lost:", info.message, info.reason);
    showError(
      `WebGPU device lost: ${info.reason ?? "unknown"}\n${info.message}`,
    );
  });

  const context = canvas.getContext("webgpu");
  if (!context) {
    throw new Error("Failed to acquire WebGPU canvas context.");
  }

  const canvasFormat = navigator.gpu.getPreferredCanvasFormat();
  context.configure({
    device,
    format: canvasFormat,
    alphaMode: "opaque",
  });

  const listeners = new Set<(w: number, h: number) => void>();
  let disposed = false;
  const ctx: GpuContext = {
    adapter,
    device,
    canvas,
    context,
    canvasFormat,
    size: { width: 1, height: 1 },
    onResize(cb) {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      ro.disconnect();
      listeners.clear();
      try {
        context.unconfigure();
      } catch {
        /* ignore */
      }
      device.destroy();
    },
  };

  const applySize = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cssW = Math.max(1, canvas.clientWidth);
    const cssH = Math.max(1, canvas.clientHeight);
    const w = Math.max(1, Math.floor(cssW * dpr));
    const h = Math.max(1, Math.floor(cssH * dpr));
    if (w === ctx.size.width && h === ctx.size.height) return;
    canvas.width = w;
    canvas.height = h;
    ctx.size = { width: w, height: h };
    for (const cb of listeners) cb(w, h);
  };

  const ro = new ResizeObserver(() => applySize());
  ro.observe(canvas);
  applySize();

  return ctx;
}

export function showError(message: string): void {
  const el = document.getElementById("error");
  if (el) {
    el.textContent = message;
    el.style.display = "block";
  }
}
